import { useLang } from 'hooks/lang';

import { loaders } from 'entities';

import {
  Button,
  Column,
  DropdownMenu,
  DropdownMenuItem,
  Input,
  Row,
  Select,
  SideSheet,
  Skeleton,
} from 'components/Blocks';
import { ReceiptLongIcon, SearchIcon } from 'components/Icons';
import { EntityStatusTag } from 'components/Entities';

import { useDebounce } from 'hooks/debounce';
import { useFormControlV2 } from 'hooks';
import { useInvoice } from '../context';

import { INVOICE_COUNTRIES } from 'utils/countries';
import { getFirst } from 'utils/array';
import { cascadeEval } from 'utils/conditions';
import { formatCurrency } from 'utils/text';

export function SearchInvoiceSheet(): JSX.Element {
  const { t } = useLang();

  const { openInvoice } = useInvoice();

  const [state, { dispatch, handleChange }] = useFormControlV2({
    country: getFirst(INVOICE_COUNTRIES).value,
    id: '',
    isOpen: false,
  });

  const searchId = useDebounce(state.id);

  const invoices = loaders.invoice.searchByAccountantId.use({
    defaultValue: [],

    blockLoad: !searchId.trim(),
    reloadOnChangeRef: [state.country, searchId].join(''),

    params: {
      country: state.country,
      id: searchId,
    },
  });

  return (
    <>
      <DropdownMenu triggerIcon={<SearchIcon size={14} />} align="end">
        <DropdownMenuItem onClick={() => dispatch({ isOpen: true })}>
          <ReceiptLongIcon size={18} />
          {t('search-by-invoice-#')}
        </DropdownMenuItem>
      </DropdownMenu>

      <SideSheet
        title={t('search-accountant-id')}
        close={() => dispatch({ isOpen: false })}
        isOpen={state.isOpen}
        gap="6"
      >
        <Row gap="2">
          <Select
            options={INVOICE_COUNTRIES.map(({ value }) => ({ value, label: value }))}
            name="country"
            onChange={handleChange}
            value={state.country}
            containerClassName="w-20 [&_svg]:hidden"
          />

          <Input value={state.id} name="id" onChange={handleChange} height={36} placeholder="#" />
        </Row>

        {cascadeEval([
          {
            is: invoices.loading,
            then: () => <Skeleton quantity={5} height={36} />,
          },

          { is: !searchId.trim(), then: () => <></> },

          { is: !invoices.data.length, then: () => <span>{t('no-invoice-found')}</span> },

          {
            is: true,
            then: () => (
              <Column>
                {invoices.data.map(({ id, clientName, currency, value, status, accountantId }) => (
                  <Button
                    className="h-auto w-full !justify-between py-2 font-normal hover:border-primary"
                    variant="outlined"
                    color="gray"
                    key={id}
                    onClick={() => openInvoice(id)}
                  >
                    <Column gap="1">
                      <span className="text-start text-sm">{clientName}</span>
                      <span className="text-start text-sm">{accountantId}</span>
                      <span className="text-start text-sm">{formatCurrency(value, currency)}</span>
                    </Column>

                    <EntityStatusTag target="invoice" status={status} className="ml-auto" />
                  </Button>
                ))}
              </Column>
            ),
          },
        ])}
      </SideSheet>
    </>
  );
}
